/**
 * Global context for keeping data that is used in multiple pages/components.
 *
 * Usage:
 *
 * const globalContext = useGlobalContext();
 * globalContext.set({'key': 'value', 'key2': 'value2', ...});
 */

import React, { ReactNode, useRef, useState } from 'react';
import { Category } from 'types/category.types';
import { HomeFeedEngine, ProductType } from 'types/product.types';
import { ShopType } from 'types/shop.types';

import { OtpTriggerSource, UserPhoneVerificationType } from '../components/UserPhoneVerificationModal/UserPhoneVerificationModal';
import { STORAGE_MODE_BUTTON_VISIBLE_KEY, STORAGE_MODE_ENABLED_KEY, STORAGE_RTL_MODE_KEY } from '../config/storage-keys';
import { IDeliveryProvince } from '../types/delivery.types';

type Props = {
  children: ReactNode | ReactNode[];
};

type GlobalContextKey = string | number;
type GlobalContextValue = unknown;

type GlobalContextInnerState = {
  set: (key: GlobalContextState) => void;
  get: (key: GlobalContextKey) => GlobalContextValue;
};

// Add keys here for typescript and better suggestion.
export type GlobalContextState = Record<GlobalContextKey, GlobalContextValue> & {
  // Home page products.
  homePageProducts?: ProductType[];
  hasMoreHomePageProducts?: boolean;
  homePageCampaignProductsActive?: boolean;
  homePageFeedEngine?: HomeFeedEngine;

  // Global product search.
  productSearchResults?: ProductType[];
  hasMoreProductSearchResults?: boolean;

  // Global shop search.
  shopSearchResults?: ShopType[];
  hasMoreShopSearchResults?: boolean;

  categorySearchShops?: ShopType[];
  categorySearchShopsHasMore?: boolean;

  // Category search.
  categorySearchProducts?: ProductType[];
  categorySearchProductsHasMore?: boolean;
  categorySearchAvailableSubCategories?: Category[];
  categorySearchCategoriesHashMap?: Record<string, any>;

  // Brand search.
  brandSearchProducts?: ProductType[];
  brandSearchProductsHasMore?: boolean;
  brandSearchAvailableCategories?: Category[];
  brandSearchSizeExpanded?: boolean;

  // Shop page.
  shopPageAvailableProducts?: ProductType[];
  shopPageHasMoreAvailableProducts?: boolean;
  shopPageSoldProducts?: ProductType[];
  shopPageHasMoreSoldProducts?: boolean;
  shopPageIsLoading?: boolean;

  pudoDeliveryLocations?: IDeliveryProvince[];
  isDownloadAppBannerHidden?: boolean;
  isRtlMode?: boolean;

  // Modals.
  phoneVerificationModal?: {
    visible: boolean;
    onClose?: (isSuccess: boolean, usedCode: string, verifiedPhone: string) => void;
    type?: UserPhoneVerificationType;
    skipSuccessView?: boolean;
    previousCode?: string;
    prefillPhoneNumber?: string;
    triggerSource?: OtpTriggerSource;
  };
};

export const GlobalContext = React.createContext({} as GlobalContextInnerState & GlobalContextState);

const GlobalContextProvider = ({ children }: Props) => {
  const [state, setState] = useState<GlobalContextState>({
    homePageProducts: [],
    homePageCampaignProductsActive: false,
    hasMoreHomePageProducts: true,

    productSearchResults: [],
    hasMoreProductSearchResults: true,

    shopSearchResults: [],
    hasMoreShopSearchResults: true,

    categoryShops: [],
    categoryShopsHasMore: true,

    categorySearchProducts: [],
    categorySearchProductsHasMore: true,
    categorySearchAvailableSubCategories: [],
    categorySearchCategoriesHashMap: {},

    brandSearchProducts: [],
    brandSearchProductsHasMore: true,
    brandSearchAvailableCategories: [],
    brandSearchSizeExpanded: false,

    shopPageAvailableProducts: [],
    shopPageHasMoreAvailableProducts: true,
    shopPageSoldProducts: [],
    shopPageHasMoreSoldProducts: true,
    shopPageIsLoading: true,

    pudoDeliveryLocations: [],
    isDownloadAppBannerHidden: true,

    phoneVerificationModal: {
      visible: false,
    },

    isTranslationKeyModeVisible: typeof window !== 'undefined' ? localStorage.getItem(STORAGE_MODE_BUTTON_VISIBLE_KEY) === 'true' : false,
    isTranslationKeyModeEnabled: typeof window !== 'undefined' ? localStorage.getItem(STORAGE_MODE_ENABLED_KEY) === 'true' : false,
    isRtlMode: typeof window !== 'undefined' ? localStorage.getItem(STORAGE_RTL_MODE_KEY) === 'true' : false,
  });
  const lastState = useRef<GlobalContextState>(state);

  const set = (addState: GlobalContextState) => {
    lastState.current = {
      ...lastState.current,
      ...(addState || {}),
    };

    setState(lastState.current);

    return lastState.current;
  };

  const get = (key: string | number) => state[key];

  return <GlobalContext.Provider value={{ set, get, ...state }}>{children}</GlobalContext.Provider>;
};

export default GlobalContextProvider;
